import type { ApiErrorBody } from '@miroom/shared';
import { ApiError } from '../../application/ports/api';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** Thin JSON fetch wrapper that maps error bodies to ApiError. */
export class HttpClient {
  constructor(private readonly baseUrl = '') {}

  async request<T>(method: HttpMethod, path: string, body?: unknown): Promise<T> {
    let res: Response;
    try {
      res = await fetch(this.baseUrl + path, {
        method,
        credentials: 'same-origin',
        headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch {
      throw new ApiError('network', 'Network error');
    }
    return this.parse<T>(res);
  }

  /** Uploads a binary body with progress (XHR, because fetch has no upload progress). */
  upload<T>(path: string, file: Blob, onProgress?: (fraction: number) => void): Promise<T> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', this.baseUrl + path);
      xhr.withCredentials = true;
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
      xhr.onerror = () => reject(new ApiError('network', 'Network error'));
      xhr.onload = () => {
        const res = new Response(xhr.responseText || null, {
          status: xhr.status,
          headers: { 'Content-Type': xhr.getResponseHeader('Content-Type') ?? '' },
        });
        this.parse<T>(res).then(resolve, reject);
      };
      xhr.send(file);
    });
  }

  private async parse<T>(res: Response): Promise<T> {
    if (res.status === 204) return undefined as T;
    const isJson = res.headers.get('Content-Type')?.includes('application/json');
    const data = isJson ? await res.json().catch(() => null) : null;
    if (!res.ok) {
      const err = (data as ApiErrorBody | null)?.error;
      throw new ApiError(
        err?.code ?? 'internal',
        err?.message ?? res.statusText,
        res.status,
        err?.fields ?? {},
      );
    }
    return data as T;
  }
}
